import React, { useContext, FocusEvent, useState, useEffect } from 'react';

import { observer } from 'mobx-react-lite';

import { RootStoreContext } from 'index';
import { AddressInformationFieldName } from 'components/intermediaryApplications/intermediaryApplications.enums';
import Button from 'components/common/Button/Button';
import { EditIcon } from 'components/common/Icons/Icons';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import styles from 'components/intermediaryApplications/CompanyProfile/CompanyProfile.module.scss';
import {
  getStreetAddressFromAddressVariable,
  getStreetNameFromStreetAddress,
  getStreetNumberFromStreetAddress,
} from 'common/utils/string.utils';
import { validateRequiredField, validateZipCode } from 'common/utils/validation.utils';
import useForm, { FieldValues } from 'hooks/useForm';
import AddressInputs from 'components/common/AddressInputs/AddressInputs';
import Checkbox from 'components/common/Checkbox/Checkbox';
import { hasPermission } from 'components/permissions/permissions.utils';
import { PermissionName } from 'components/permissions/permissions.enum';

const AddressInformation = () => {
  const { intermediaryApplicationsStore, authStore } = useContext(RootStoreContext);
  const { companyProfile, updateAddress, isLoading } = intermediaryApplicationsStore;
  const [isEditDataActive, setIsEditDataActive] = useState<boolean>(false);
  const [isMailingAddressEqualToCorporate, setIsMailingAddressEqualToCorporate] = useState<boolean>(false);
  const userPermissions = authStore.account?.permissions ?? [];
  const canCompanyInformationUpdate = hasPermission(userPermissions, [PermissionName.CompanyInformationUpdate]);
  const defaultValues = {
    [AddressInformationFieldName.CorporateAddress]:
      getStreetAddressFromAddressVariable(companyProfile?.corporateAddressInformation) || '',
    [AddressInformationFieldName.CorporateCity]: companyProfile?.corporateAddressInformation?.city || '',
    [AddressInformationFieldName.CorporateState]: companyProfile?.corporateAddressInformation?.state_or_province || '',
    [AddressInformationFieldName.CorporateZip]: companyProfile?.corporateAddressInformation?.zip_or_postal_code || '',
    [AddressInformationFieldName.MailingAddress]:
      getStreetAddressFromAddressVariable(companyProfile?.mailingAddressInformation) || '',
    [AddressInformationFieldName.MailingCity]: companyProfile?.mailingAddressInformation?.city || '',
    [AddressInformationFieldName.MailingState]: companyProfile?.mailingAddressInformation?.state_or_province || '',
    [AddressInformationFieldName.MailingZip]: companyProfile?.mailingAddressInformation?.zip_or_postal_code || '',
  };

  const { errors, values, setValue, setValues, validateField, handleSubmit, resetForm } = useForm<FieldValues>({
    defaultValues: defaultValues,
    handleValidation: (fieldName, value) => {
      switch (fieldName) {
        case AddressInformationFieldName.CorporateAddress: {
          return validateRequiredField(value, fieldName);
        }
        case AddressInformationFieldName.CorporateCity: {
          return validateRequiredField(value, fieldName);
        }
        case AddressInformationFieldName.CorporateState: {
          return validateRequiredField(value, fieldName);
        }
        case AddressInformationFieldName.CorporateZip: {
          return validateRequiredField(value, fieldName) || validateZipCode(value, fieldName);
        }
        case AddressInformationFieldName.MailingAddress: {
          return validateRequiredField(value, fieldName);
        }
        case AddressInformationFieldName.MailingCity: {
          return validateRequiredField(value, fieldName);
        }
        case AddressInformationFieldName.MailingState: {
          return validateRequiredField(value, fieldName);
        }
        case AddressInformationFieldName.MailingZip: {
          return validateRequiredField(value, fieldName) || validateZipCode(value, fieldName);
        }
      }

      return '';
    },
  });

  const handleEditData = () => {
    setIsEditDataActive(true);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    const { name } = event.target;

    validateField(name, values[name]);
  };

  const handleSaveInformation = handleSubmit(async () => {
    try {
      await updateAddress({
        corporateAddressInformation: {
          streetNumber: getStreetNumberFromStreetAddress(values[AddressInformationFieldName.CorporateAddress]) || null,
          streetName: getStreetNameFromStreetAddress(values[AddressInformationFieldName.CorporateAddress]) || null,
          city: values[AddressInformationFieldName.CorporateCity] || null,
          stateOrProvince: values[AddressInformationFieldName.CorporateState] || null,
          zipOrPostalCode: values[AddressInformationFieldName.CorporateZip] || null,
        },
        mailingAddressInformation: {
          streetNumber: getStreetNumberFromStreetAddress(values[AddressInformationFieldName.MailingAddress]) || null,
          streetName: getStreetNameFromStreetAddress(values[AddressInformationFieldName.MailingAddress]) || null,
          city: values[AddressInformationFieldName.MailingCity] || null,
          stateOrProvince: values[AddressInformationFieldName.MailingState] || null,
          zipOrPostalCode: values[AddressInformationFieldName.MailingZip] || null,
        },
      });

      notify(ToastMessage.InformationSaved, 'success');
      setIsEditDataActive(false);
    } catch (error: any) {
      notify(error.message || ToastMessage.DefaultError, 'error');
    }
  });

  const handleCancelEditData = () => {
    resetForm();
    setIsEditDataActive(false);
  };

  const handleMailingAddressEqualsToCorporate = (): void => {
    if (!isMailingAddressEqualToCorporate) {
      setValues({
        [AddressInformationFieldName.MailingAddress]: values[AddressInformationFieldName.CorporateAddress],
        [AddressInformationFieldName.MailingCity]: values[AddressInformationFieldName.CorporateCity],
        [AddressInformationFieldName.MailingState]: values[AddressInformationFieldName.CorporateState],
        [AddressInformationFieldName.MailingZip]: values[AddressInformationFieldName.CorporateZip],
      });
    }

    setIsMailingAddressEqualToCorporate(!isMailingAddressEqualToCorporate);
  };

  useEffect(() => {
    setIsMailingAddressEqualToCorporate(
      defaultValues[AddressInformationFieldName.CorporateAddress] ===
        defaultValues[AddressInformationFieldName.MailingAddress] &&
        defaultValues[AddressInformationFieldName.CorporateCity] ===
          defaultValues[AddressInformationFieldName.MailingCity] &&
        defaultValues[AddressInformationFieldName.CorporateState] ===
          defaultValues[AddressInformationFieldName.MailingState] &&
        defaultValues[AddressInformationFieldName.CorporateZip] ===
          defaultValues[AddressInformationFieldName.MailingZip],
    );
  }, []);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.inputsContainer}>
        <div className={styles.header}>
          <div className={styles.headerLabel}>Company Address</div>
          {!isEditDataActive && canCompanyInformationUpdate && (
            <Button onClick={handleEditData} className={styles.button} variant="link">
              <EditIcon /> Edit Data
            </Button>
          )}
        </div>
        <AddressInputs
          values={values}
          errors={errors}
          onChange={(name: string, value: any) => setValue(name, value)}
          onBlur={handleBlur}
          onInputChange={(value: any) => {
            setValues({
              [AddressInformationFieldName.CorporateAddress]: value.streetAddress,
              [AddressInformationFieldName.CorporateCity]: value.city,
              [AddressInformationFieldName.CorporateState]: value.state,
              [AddressInformationFieldName.CorporateZip]: value.zip,
            });
          }}
          disabled={!isEditDataActive}
          useRequiredSymbol={isEditDataActive}
          addressLabels={{
            address: AddressInformationFieldName.CorporateAddress,
            city: AddressInformationFieldName.CorporateCity,
            state: AddressInformationFieldName.CorporateState,
            zip: AddressInformationFieldName.CorporateZip,
          }}
        />
        {isEditDataActive && (
          <div className={styles.checkbox}>
            <Checkbox
              isChecked={isMailingAddressEqualToCorporate}
              onChange={handleMailingAddressEqualsToCorporate}
              label={'The same address for mailing'}
            />
          </div>
        )}
        <AddressInputs
          values={values}
          errors={errors}
          onChange={(name: string, value: any) => setValue(name, value)}
          onBlur={handleBlur}
          onInputChange={(value: any) => {
            setValues({
              [AddressInformationFieldName.MailingAddress]: value.streetAddress,
              [AddressInformationFieldName.MailingCity]: value.city,
              [AddressInformationFieldName.MailingState]: value.state,
              [AddressInformationFieldName.MailingZip]: value.zip,
            });
          }}
          disabled={!isEditDataActive || isMailingAddressEqualToCorporate}
          useRequiredSymbol={!!isEditDataActive}
          addressLabels={{
            address: AddressInformationFieldName.MailingAddress,
            city: AddressInformationFieldName.MailingCity,
            state: AddressInformationFieldName.MailingState,
            zip: AddressInformationFieldName.MailingZip,
          }}
        />
        <div className={styles.formInput}>
          {isEditDataActive && (
            <div className={styles.editButtons}>
              <Button isLoading={isLoading} onClick={handleSaveInformation} className={styles.saveButton}>
                Save Changes
              </Button>
              <Button className={styles.cancelButton} onClick={handleCancelEditData}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(AddressInformation);
